'use client';

import type {
  PortalOfferSubscriptionAddonType,
  PortalOfferSubscriptionType
} from '@portal/pages/api/offer';
import { fetcher } from '@portal/shared/utils/fetcher';
import { isPreviewRouteView } from '@portal/shared/utils/previewRouteHelpers';
import {
  getOfferInvalidReason,
  isSupportedCheckoutFlowType,
  type Offer,
  type ShopifyVariantsByStoreCode,
  type UnhandledOfferInvalidReason
} from '@portal/types/offer';
import { useRouter } from 'next/router';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useLocale } from 'shared/hooks/useLocale';
import { UnifiedButtonType } from 'shared/studio/UnifiedButton/UnifiedButton.types';
import { getSanityLinkUrl } from 'shared/utils/getSanityLinkUrl';
import { stripUrlHashAndQuery } from 'state/utils/urlHelpers';
import { PickExactFields } from 'types/helpers';
import { PageResult } from 'types/studio/page';
import { Hero, HeroHalfV5, OfferV3 } from 'types/studio/studio';
import { Database } from 'types/supabase';

type PageContextType = ReturnType<typeof useCreatePageContext>;

export type CheckoutFlow = {
  type: string;
};

const parseCheckoutFlow = (value: CheckoutFlow, offerId: number): `/${string}${string}` | null => {
  const { type } = value;
  if (isSupportedCheckoutFlowType(type)) {
    return `/offer/${type}/${offerId}`;
  }
  return null;
};

type SupabasePage = Database['public']['Tables']['Page Repository']['Row'];

const COMMON_FIELDS = ['frictionless', 'use_pdp', 'pdp_url'] satisfies (
  | keyof SupabasePage
  | keyof PageResult
)[];
type CommonFields = (typeof COMMON_FIELDS)[number];
type PartialPage =
  // Atlas pages
  | PickExactFields<SupabasePage, CommonFields | 'v53Page'>
  // Sanity pages
  | PickExactFields<PageResult, CommonFields | 'v53Page'>;

type UnifiedButtonParam =
  | undefined
  | null
  | UnifiedButtonType
  | OfferV3['unifiedButton']
  | HeroHalfV5['unifiedButton']
  | Hero[
      | 'heroAnimatedUnifiedButton'
      | 'heroFullUnifiedButton'
      | 'heroHalfTestUnifiedButton'
      | 'heroHalfV5UnifiedButton'];

type LinkParam = string | null | undefined;
interface LinkOptions {
  noEmpty?: boolean;
}

// Default context values should allow `.stories.tsx` components to work
// properly.
const PageContext = createContext<PageContextType>({
  page: {} as PartialPage,
  getOfferCTALink: () => null,
  sectionsUsed: null,
  offerCTALink: null,
  disablePrimaryCTAButtons: false,
  shouldDisableCTALink: (_link: LinkParam, _options: LinkOptions = {}) => false,
  shouldDisableUnifiedButton: (_button: UnifiedButtonParam) => false,
  maybeReplaceOfferlessCheckoutLink: (link: string | null | undefined) => link || undefined,
  offer: null,
  subscriptionTypes: null,
  addonTypes: null,
  baseOffer: null,
  baseOfferAddonTypes: null,
  offerInvalidReason: null,
  baseOfferSubscriptionTypes: null,
  shopifyVariantsByStoreCode: null
});

export const usePageContext = (): PageContextType => {
  const context = useContext(PageContext);
  if (context === undefined) {
    throw new Error('usePageContext must be used within a PageProvider');
  }
  return context;
};

const FRICTIONLESS_PARAMS = ['variant_id', 'promo_code'] as const;

export type PageContextProps = {
  page: PartialPage;
  /**
   * Undefined values indicate that we're unsure of the contents of the page.
   */
  sectionsUsed: {
    offerAnchor: boolean;
    navigation?: boolean;
    sanityOffer: boolean;
    builderOffer: boolean;
  } | null;
  checkoutFlow?: CheckoutFlow | null;
  offerId?: number | null;
  offer?: Offer | null;
  subscriptionTypes?: PortalOfferSubscriptionType[] | null;
  addonTypes?: PortalOfferSubscriptionAddonType[] | null;
  baseOffer?: Offer | null;
  baseOfferSubscriptionTypes: PortalOfferSubscriptionType[] | null;
  baseOfferAddonTypes: PortalOfferSubscriptionAddonType[] | null;
  shopifyVariantsByStoreCode: ShopifyVariantsByStoreCode | null;
  offerInvalidReason?: UnhandledOfferInvalidReason | null;
};

export const isOfferlessCheckoutLink = (link: string) =>
  /\/(build-a-bundle|cart\/042)(-\w+)*$/.test(stripUrlHashAndQuery(link));

const useCreatePageContext = ({
  page,
  sectionsUsed,
  checkoutFlow,
  offerId,
  addonTypes: addonTypesProp,
  offer: offerProp,
  subscriptionTypes: subscriptionTypesProp,
  baseOffer,
  baseOfferSubscriptionTypes,
  baseOfferAddonTypes,
  shopifyVariantsByStoreCode,
  offerInvalidReason: offerInvalidReasonProp
}: PageContextProps) => {
  const router = useRouter();
  const locale = useLocale();
  const { frictionless } = page;

  const [offer, setOffer] = useState(offerProp);
  const [subscriptionTypes, setSubscriptionTypes] = useState(subscriptionTypesProp);
  const [addonTypes, setAddonTypes] = useState(addonTypesProp);

  const offerIdLoaded = useRef(offerProp?.id || null);

  useEffect(() => {
    const updateOffer = async () => {
      let currentOffer = offerProp || offer;
      // Fetch offer only if it's not already loaded.
      if (offerId && offerIdLoaded.current !== offerId) {
        offerIdLoaded.current = offerId;
        const response = await fetcher.get<Offer | undefined>(
          `/api/offer?id=${offerId}&locale=${locale}`,
          undefined,
          {
            omitCookies: true
          }
        );
        currentOffer = response.data;
      }

      if (!currentOffer) {
        setOffer(offerProp);
        setSubscriptionTypes(subscriptionTypesProp);
        setAddonTypes(addonTypesProp);
        return;
      }

      setOffer(currentOffer);
      setSubscriptionTypes(
        // 'type' checks are necessary because of the way the data is fetched
        // between Atlas and Portal. Atlas should be passing data through the prop,
        // but Portal will fetch and we can use the 'type' directly.
        subscriptionTypesProp ||
          currentOffer.subscriptions.reduce(
            (acc, x) => ('type' in x && x.type ? [...acc, x.type] : acc),
            [] as PortalOfferSubscriptionType[]
          )
      );
      setAddonTypes(
        addonTypesProp ||
          currentOffer.subscriptions.flatMap((i) =>
            i.addons.reduce(
              (acc, x) => ('type' in x && x.type ? [...acc, x.type] : acc),
              [] as PortalOfferSubscriptionAddonType[]
            )
          )
      );
    };
    updateOffer();
  }, [
    addonTypesProp,
    baseOfferAddonTypes,
    baseOfferSubscriptionTypes,
    locale,
    offer,
    offerId,
    offerProp,
    subscriptionTypesProp
  ]);

  const offerInvalidReason = useMemo(() => {
    if (offerInvalidReasonProp) {
      return offerInvalidReasonProp;
    }
    if (offer) {
      return getOfferInvalidReason(offer, locale);
    }
    return null;
  }, [locale, offer, offerInvalidReasonProp]);

  const disablePrimaryCTAButtons =
    frictionless === true &&
    FRICTIONLESS_PARAMS.length > 0 &&
    !FRICTIONLESS_PARAMS.every((param) => !!router.query[param]) &&
    !offerInvalidReason;

  const shouldDisableCTALink = useCallback(
    (link: LinkParam, { noEmpty = false }: LinkOptions = {}) => {
      if (!link) {
        return noEmpty && !disablePrimaryCTAButtons;
      }

      if (!disablePrimaryCTAButtons) {
        return false;
      }

      return isOfferlessCheckoutLink(link) || link.endsWith('#offers');
    },
    [disablePrimaryCTAButtons]
  );

  const shouldDisableUnifiedButton = useCallback(
    (button: UnifiedButtonParam) => {
      if (!button) {
        return false;
      }

      // eslint-disable-next-line default-case
      switch (button.switchButton) {
        case undefined:
        case 'goToOfferButton':
        case 'noButton':
        case 'scrollButton':
          return disablePrimaryCTAButtons;

        case 'link':
          return (
            shouldDisableCTALink(button.buttonLink || '') ||
            ('baseButtonLinkV2' in button &&
              shouldDisableCTALink(getSanityLinkUrl(button.baseButtonLinkV2?.btnLink, locale), {
                noEmpty: true
              }))
          );

        case 'openModalButton':
          if ('openModalSelect' in button) {
            switch (button.openModalSelect) {
              case 'ingredients':
                return false;

              default: {
                return false;
              }
            }
          } else {
            return false;
          }

        default:
          return false;
      }
    },
    [disablePrimaryCTAButtons, locale, shouldDisableCTALink]
  );

  const isPreviewRoute = isPreviewRouteView(router);

  const getOfferCTALink = useCallback(
    (options?: { fallbackNoOffer?: string; fallbackInvalidOffer?: string }) => {
      const usedOfferId = offerId ?? offer?.id;
      if (!usedOfferId) {
        return options?.fallbackNoOffer || null;
      }
      if (!isPreviewRoute && disablePrimaryCTAButtons) {
        return null;
      }
      if (
        offerInvalidReason !== null ||
        // a page should only be considered having an invalid offer if both
        // the offer section and the offer anchor are not rendered
        // if either is present, parse the URL as usual
        (!sectionsUsed?.builderOffer && !sectionsUsed?.offerAnchor) ||
        !offer
      ) {
        return options?.fallbackInvalidOffer || null;
      }

      // Default to 'bab' just in case a page was somehow saved without a checkout
      // flow.
      const safeCheckoutFlow = checkoutFlow ?? ({ type: 'bab' } satisfies CheckoutFlow);

      return parseCheckoutFlow(safeCheckoutFlow, usedOfferId);
    },
    [
      checkoutFlow,
      disablePrimaryCTAButtons,
      isPreviewRoute,
      offerId,
      offer,
      offerInvalidReason,
      sectionsUsed?.builderOffer,
      sectionsUsed?.offerAnchor
    ]
  );

  const maybeReplaceOfferlessCheckoutLink = useCallback(
    (link: string | null | undefined) => {
      if (page.use_pdp && page.pdp_url) {
        return page.pdp_url;
      }

      if (sectionsUsed?.builderOffer && link && isOfferlessCheckoutLink(link)) {
        return getOfferCTALink({ fallbackInvalidOffer: link }) || undefined;
      }
      return link || undefined;
    },
    [getOfferCTALink, sectionsUsed, page]
  );

  const offerCTALink = useMemo(
    () => getOfferCTALink({ fallbackInvalidOffer: '/offer/bab/1' }),
    [getOfferCTALink]
  );

  return useMemo(
    () => ({
      page,
      getOfferCTALink,
      offerCTALink,
      sectionsUsed,
      disablePrimaryCTAButtons,
      shouldDisableUnifiedButton,
      shouldDisableCTALink,
      maybeReplaceOfferlessCheckoutLink,
      offer,
      subscriptionTypes,
      addonTypes,
      baseOffer,
      baseOfferAddonTypes,
      offerInvalidReason,
      baseOfferSubscriptionTypes,
      shopifyVariantsByStoreCode
    }),
    [
      page,
      getOfferCTALink,
      offerCTALink,
      sectionsUsed,
      disablePrimaryCTAButtons,
      shouldDisableUnifiedButton,
      shouldDisableCTALink,
      maybeReplaceOfferlessCheckoutLink,
      offer,
      subscriptionTypes,
      addonTypes,
      baseOffer,
      baseOfferAddonTypes,
      offerInvalidReason,
      baseOfferSubscriptionTypes,
      shopifyVariantsByStoreCode
    ]
  );
};

export const PageProvider: React.FC<PageContextProps & { children: React.ReactNode }> = ({
  children,
  ...restProps
}) => {
  const baseSectionsContext = useCreatePageContext(restProps);

  return <PageContext.Provider value={baseSectionsContext}>{children}</PageContext.Provider>;
};
